import { readBoundedRegularFile } from './byte-budget';
import {
  MAX_REBUTTAL_RESPONSE_CHARS,
  MAX_REVIEW_DESCRIPTION_CHARS,
  MAX_REVIEW_FILE_CHARS,
  MAX_REVIEW_ITEMS,
  MAX_REVIEW_SUMMARY_CHARS,
  ReviewFollowupRecommendation,
  ReviewIssue,
  ReviewResult,
} from './schema';
import { fencedBlock, truncate } from './utils';

export interface PassResult {
  key: string;
  title: string;
  exitCode: number;
  attempt: number;
  maxAttempts: number;
  durationMs: number;
  eventsFile: string;
  stderrFile: string;
  eventCount: number;
  commandCount: number;
  usageSummary: string | null;
  stderrTail: string | null;
  failureDiagnostic: string | null;
  diagnosticsTruncated: boolean;
  review: ReviewResult | null;
  failure: string | null;
}

const MAX_GITHUB_COMMENT_CHARS = 60000;

export function parseReviewOutput(file: string, maxBytes = 1048576): ReviewResult {
  if (!Number.isSafeInteger(maxBytes) || maxBytes < 1) {
    throw new Error('Review output byte limit must be a positive integer.');
  }
  const raw = readBoundedRegularFile({
    file,
    maxBytes,
    description: 'Review output',
    limitName: 'max-review-output-bytes',
  }).toString('utf8');
  return normalizeReview(JSON.parse(raw));
}

export function normalizeReview(value: unknown): ReviewResult {
  if (!value || typeof value !== 'object') {
    throw new Error('Review output must be a JSON object.');
  }
  const candidate = value as Partial<ReviewResult>;
  const allowedKeys = new Set([
    'verdict',
    'blocking_issues',
    'followup_recommendations',
    'summary',
    'rebuttal_response',
    'close_recommended',
  ]);
  const unknownKeys = Object.keys(value).filter((key) => !allowedKeys.has(key));
  if (unknownKeys.length > 0) {
    throw new Error(`Review output contains unknown field(s): ${unknownKeys.join(', ')}.`);
  }
  if (candidate.verdict !== 'APPROVE' && candidate.verdict !== 'BLOCK') {
    throw new Error(`Review output has unknown verdict: ${String(candidate.verdict)}`);
  }
  if (!Array.isArray(candidate.blocking_issues)) {
    throw new Error('Review output blocking_issues must be an array.');
  }
  if (!Array.isArray(candidate.followup_recommendations)) {
    throw new Error('Review output followup_recommendations must be an array.');
  }
  if (typeof candidate.summary !== 'string') {
    throw new Error('Review output summary must be a string.');
  }
  if (typeof candidate.rebuttal_response !== 'string') {
    throw new Error('Review output rebuttal_response must be a string.');
  }
  if (typeof candidate.close_recommended !== 'boolean') {
    throw new Error('Review output close_recommended must be a boolean.');
  }
  const blockingIssueValues = candidate.blocking_issues;
  const followupValues = candidate.followup_recommendations;
  if (blockingIssueValues.length > MAX_REVIEW_ITEMS) {
    throw new Error(`Review output contains more than ${MAX_REVIEW_ITEMS} blocking issues.`);
  }
  if (followupValues.length > MAX_REVIEW_ITEMS) {
    throw new Error(`Review output contains more than ${MAX_REVIEW_ITEMS} follow-up recommendations.`);
  }
  const blockingIssues = blockingIssueValues.map(normalizeIssue);
  const followupRecommendations = followupValues.map(normalizeFollowupRecommendation);
  const boundedSummary = boundedString(candidate.summary, 'review summary', MAX_REVIEW_SUMMARY_CHARS, true);
  const summary = boundedSummary.trim() !== ''
    ? boundedSummary
    : '(no summary)';
  const rebuttalResponse = boundedString(candidate.rebuttal_response, 'rebuttal response', MAX_REBUTTAL_RESPONSE_CHARS, true);
  return {
    verdict: candidate.verdict === 'APPROVE' && blockingIssues.length > 0 ? 'BLOCK' : candidate.verdict,
    blocking_issues: blockingIssues,
    followup_recommendations: followupRecommendations,
    summary,
    rebuttal_response: rebuttalResponse,
    close_recommended: candidate.close_recommended,
  };
}

export function aggregatePasses(results: PassResult[]): {
  verdict: 'APPROVE' | 'BLOCK' | 'FAILED';
  blockingCount: number;
  blockingIssues: ReviewIssue[];
  followupRecommendations: ReviewFollowupRecommendation[];
} {
  const reviews = results.flatMap((result) => result.review ? [result.review] : []);
  const blockingReviews = reviews.filter((review) => review.verdict === 'BLOCK');
  const blockingIssues = blockingReviews.flatMap((review) => review.blocking_issues);
  const failed = results.length === 0
    || results.some((result) => result.failure !== null || result.review === null);
  return {
    verdict: failed ? 'FAILED' : blockingReviews.length > 0 ? 'BLOCK' : 'APPROVE',
    blockingCount: blockingIssues.length,
    blockingIssues,
    followupRecommendations: reviews.flatMap((review) => review.followup_recommendations),
  };
}

export function renderPassComment({
  headSha,
  baseSha,
  result,
}: {
  headSha: string;
  baseSha: string;
  result: PassResult;
}): string {
  const failed = result.failure !== null || result.review === null;
  const lines = [
    `<!-- codex-review:pass=${result.title.toLowerCase().replace(/\s+/g, '-')} head=${headSha} base=${baseSha} -->`,
    `## ${result.title}${failed ? ' - FAILED' : ''}`,
    '',
  ];
  if (failed) {
    const failure = result.failure || 'Review did not produce a verdict.';
    lines.push(
      ':x: The reviewer did not complete, so this pass produced no approval or blocker verdict. CI failed closed.',
      '',
      '### Why it failed',
      '',
      failure,
      '',
      `Exit code: \`${result.exitCode}\``,
      `Attempt: \`${result.attempt}/${result.maxAttempts}\``,
      `Runtime: \`${formatDuration(result.durationMs)}\``,
    );
    if (result.failureDiagnostic) {
      lines.push(
        '',
        '<details>',
        '<summary>Reviewer diagnostic</summary>',
        '',
        fencedBlock('text', result.failureDiagnostic),
        '',
        '</details>',
      );
    } else if (result.exitCode !== 0) {
      lines.push(
        '',
        `Codex emitted no fatal diagnostic message before exiting after ${result.eventCount} event(s) and ${result.commandCount} command event(s).`,
      );
    }
    if (result.diagnosticsTruncated) {
      lines.push('', '_The stored diagnostics reached the configured size limit and were truncated._');
    }
    return boundedComment(lines.join('\n'));
  }
  const review = result.review;
  lines.push('### Blocking Issues', '');
  const issues = review?.blocking_issues || [];
  lines.push(issues.length > 0 ? issues.map(renderIssue).join('\n') : '- None.');
  lines.push('', '### Follow-up Recommendations', '');
  const followupRecommendations = review?.followup_recommendations || [];
  lines.push(followupRecommendations.length > 0
    ? followupRecommendations.map(renderFollowupRecommendation).join('\n')
    : '- None.');
  if (review && review.rebuttal_response.trim() !== '') {
    lines.push('', '### Rebuttal Response', '', truncate(review.rebuttal_response, 2500));
  }
  lines.push('', '### Summary', '', review?.summary || '(no summary)');
  return boundedComment(lines.join('\n'));
}

export function renderAggregateComment({
  headSha,
  baseSha,
  results,
}: {
  headSha: string;
  baseSha: string;
  results: PassResult[];
}): string {
  const aggregate = aggregatePasses(results);
  const decision = aggregate.verdict === 'APPROVE'
    ? 'ACCEPT'
    : aggregate.verdict === 'BLOCK' ? 'REJECT' : 'FAILED';
  const passSummary = results
    .map((result) => `${result.key} ${result.failure !== null || result.review === null
      ? 'FAILED'
      : result.review.verdict}`)
    .join(', ');
  const lines = [
    `<!-- codex-review:aggregate head=${headSha} base=${baseSha} -->`,
    '## Aggregated Codex Review Result',
    '',
    `Codex Review: ${decision} - ${passSummary}.`,
    '',
  ];
  const failures = results.filter((result) => result.failure !== null || result.review === null);
  if (failures.length > 0) {
    lines.push(
      'No aggregate approval or rejection was produced because every configured pass must return a review verdict. CI failed closed.',
      '',
    );
    lines.push('### Review Failures', '');
    for (const result of failures) {
      lines.push(`- ${result.title}: ${result.failure || 'Review did not produce a verdict.'}`);
      if (result.failureDiagnostic) {
        lines.push(
          '',
          '<details>',
          `<summary>${result.title} diagnostic</summary>`,
          '',
          fencedBlock('text', result.failureDiagnostic),
          '',
          '</details>',
          '',
        );
      }
    }
  }
  if (aggregate.verdict === 'BLOCK') {
    lines.push(
      '### Rebuttal',
      '',
      'To ask Codex Review to reconsider these blockers, post a PR comment beginning with `/rebuttal`.',
      '',
    );
    appendMachineReadableCollection(
      lines,
      'Blocking issues JSON',
      { blocking_issues: aggregate.blockingIssues },
    );
  } else {
    const allPassesApproved = results.length > 0
      && results.every((result) => result.review?.verdict === 'APPROVE');
    if (allPassesApproved && aggregate.followupRecommendations.length > 0) {
      appendMachineReadableCollection(
        lines,
        'Follow-up recommendations JSON',
        { followup_recommendations: aggregate.followupRecommendations },
      );
    }
  }

  return boundedComment(lines.join('\n'));
}

function formatDuration(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

function appendMachineReadableCollection(
  lines: string[],
  summary: string,
  value: Record<string, unknown>,
): void {
  lines.push(
    '<details>',
    `<summary>${summary}</summary>`,
    '',
    '```json',
    JSON.stringify(value, null, 2),
    '```',
    '',
    '</details>',
  );
}

function normalizeIssue(value: unknown): ReviewIssue {
  if (!value || typeof value !== 'object') {
    throw new Error('Blocking issue must be an object.');
  }
  assertAllowedObjectKeys(value, ['file', 'line', 'description'], 'blocking issue');
  const issue = value as Partial<ReviewIssue>;
  return {
    file: boundedString(requireString(issue.file, 'blocking issue file'), 'blocking issue file', MAX_REVIEW_FILE_CHARS),
    line: requirePositiveLine(issue.line, 'blocking issue line'),
    description: boundedString(requireString(issue.description, 'blocking issue description'), 'blocking issue description', MAX_REVIEW_DESCRIPTION_CHARS),
  };
}

function normalizeFollowupRecommendation(value: unknown): ReviewFollowupRecommendation {
  if (!value || typeof value !== 'object') {
    throw new Error('Follow-up recommendation must be an object.');
  }
  assertAllowedObjectKeys(value, ['file', 'line', 'description'], 'follow-up recommendation');
  const recommendation = value as Partial<ReviewFollowupRecommendation>;
  return {
    file: boundedString(requireString(recommendation.file, 'follow-up recommendation file'), 'follow-up recommendation file', MAX_REVIEW_FILE_CHARS),
    line: requirePositiveLine(recommendation.line, 'follow-up recommendation line'),
    description: boundedString(requireString(recommendation.description, 'follow-up recommendation description'), 'follow-up recommendation description', MAX_REVIEW_DESCRIPTION_CHARS),
  };
}

function assertAllowedObjectKeys(value: object, allowed: string[], description: string): void {
  const allowedKeys = new Set(allowed);
  const unknownKeys = Object.keys(value).filter((key) => !allowedKeys.has(key));
  if (unknownKeys.length > 0) {
    throw new Error(`${description} contains unknown field(s): ${unknownKeys.join(', ')}.`);
  }
}

function requireString(value: unknown, name: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} must be a non-empty string.`);
  }
  return value;
}

function requirePositiveLine(value: unknown, name: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) {
    throw new Error(`${name} must be a positive integer.`);
  }
  return Number(value);
}

function boundedString(value: string, name: string, maxChars: number, allowEmpty = false): string {
  if (!allowEmpty && value.trim() === '') {
    throw new Error(`${name} must be a non-empty string.`);
  }
  if (value.length > maxChars) {
    throw new Error(`${name} exceeds ${maxChars} characters.`);
  }
  return value;
}

function boundedComment(value: string): string {
  if (value.length > MAX_GITHUB_COMMENT_CHARS) {
    throw new Error(`Rendered review comment exceeds ${MAX_GITHUB_COMMENT_CHARS} characters.`);
  }
  return value;
}

function renderIssue(issue: ReviewIssue): string {
  return `- \`${issue.file}:${issue.line}\` - ${truncate(issue.description)}`;
}

function renderFollowupRecommendation(recommendation: ReviewFollowupRecommendation): string {
  return `- \`${recommendation.file}:${recommendation.line}\` - ${truncate(recommendation.description)}`;
}
